import { Feature } from 'ol';
import type { Coordinate } from 'ol/coordinate';
import { Polyline } from 'ol/format';
import type { Geometry } from 'ol/geom';
import { Point } from 'ol/geom';
import type VectorSource from 'ol/source/Vector';
import { defineStore } from 'pinia';
import {
  computed,
  ref,
  shallowRef,
  toRaw,
} from 'vue';

import type { GeoJsonStylePropertiesDTO, GetRoute200Response } from '@nora-monorepo/nora-api-client';
import type {
  NamedLatLonCoordinate,
} from '@nora-monorepo/shared';
import {
  createRouteErrorFromApiResponse,
  isNonEmptyString,
  NoraSupportedCoordinateFormats,
  RouteError,
  RouteErrorType,
} from '@nora-monorepo/shared';
import { t, useApiStore, useToastStore } from '@nora-monorepo/ui-shared';

import { namedLatLonCoordinateToOlCoordinate, olToNamedLatLonCoordinate } from '#nora-ui/map/drawtools/draw-tool-utils';
import { getRoutePointStyle } from '#nora-ui/map/drawtools/styles/route-style';
import { getFeatureStyle } from '#nora-ui/map/drawtools/styles/styles';
import { isFeature } from '#nora-ui/map/feature-properties';

export enum RouteViewMode {
  PointList = 'points',
  Address = 'address',
}

function routeToolStore() {
  const api = useApiStore();
  const toastStore = useToastStore();

  const currentError = ref<RouteError | null>(null);
  const featureId = ref<string | undefined>(undefined);
  // shallowRef: OL Features are heavy and should not be proxy-wrapped by Vue.
  const hoveredPointFeature = shallowRef<Feature | null>(null);
  // Stays a deep ref: mutated in place (push/splice) and consumed reactively, only a few points.
  const pointFeatures = ref<Feature[]>([]);

  // The coordinates that make up the route.
  const routeCoordinates = ref<NamedLatLonCoordinate[]>([]);
  const routeFeature = shallowRef<Feature | null>(null);
  const viewMode = ref<RouteViewMode>(RouteViewMode.PointList);

  const clearErrors = (): void => {
    currentError.value = null;
  };

  /**
   * Resets the route, but does not reset the whole store.
   */
  const resetRoute = (): void => {
    routeCoordinates.value = [];
    pointFeatures.value = [];
    hoveredPointFeature.value = null;
    routeFeature.value = null;
    clearErrors();
  };

  /**
   * Resets the entire route tool store state so it is ready to be re-used.
   */
  const reset = (): void => {
    resetRoute();
    featureId.value = undefined;
    viewMode.value = RouteViewMode.PointList;
  };

  const getErrorMessage = (error: RouteError): { message: string; detail: string } => {
    let message: string;
    let detail: string;

    switch (error.errorType) {
      case RouteErrorType.ServiceUnavailable:
        message = t('drawTools.route.errors.serviceUnavailable');
        detail = t('drawTools.route.errors.serviceUnavailableDetail');
        break;
      case RouteErrorType.ConnectionNotFound:
        message = t('drawTools.route.errors.connectionNotFound');
        detail = t('drawTools.route.errors.connectionNotFoundDetail');
        break;
      case RouteErrorType.NotEnoughPoints:
        message = t('drawTools.route.errors.notEnoughPoints');
        detail = t('drawTools.route.errors.notEnoughPointsDetail');
        break;
      case RouteErrorType.PointNotFound:
        message = t('drawTools.route.errors.pointNotFound', (error.pointIndex ?? 0) + 1);
        detail = t('drawTools.route.errors.pointNotFoundDetail');
        break;
      case RouteErrorType.PointOutOfBounds:
        message = t('drawTools.route.errors.pointOutOfBounds', (error.pointIndex ?? 0) + 1);
        detail = t('drawTools.route.errors.pointOutOfBoundsDetail');
        break;
      case RouteErrorType.PointDistanceExceeded:
        message = t('drawTools.route.errors.pointDistanceExceeded', (error.pointIndex ?? 0) + 1);
        detail = t('drawTools.route.errors.pointDistanceExceededDetail');
        break;
      case RouteErrorType.TooManyPoints:
        message = t('drawTools.route.errors.tooManyPoints');
        detail = t('drawTools.route.errors.tooManyPointsDetail', error.maxAllowedPoints ?? 5);
        break;
      case RouteErrorType.IllegalArgument:
        message = t('drawTools.route.errors.illegalArgument');
        detail = t('drawTools.route.errors.illegalArgumentDetail');
        break;
      case RouteErrorType.RuntimeError:
        message = t('drawTools.route.errors.runtimeError');
        detail = t('drawTools.route.errors.runtimeErrorDetail');
        break;
      case RouteErrorType.RateLimitExceeded: {
        message = t('drawTools.route.errors.rateLimitExceeded');
        detail = t('drawTools.route.errors.rateLimitExceededDetail');
        break;
      }
      case RouteErrorType.Unknown:
      default:
        message = t('drawTools.route.errors.unknown');
        detail = t('drawTools.route.errors.unknownDetail');
        break;
    }
    return { message, detail };
  };

  const errorMessage = computed(() => {
    if (!currentError.value) {
      return null;
    }
    return getErrorMessage(currentError.value);
  });

  const showRouteErrorToast = (error: RouteError): void => {
    const { message, detail } = getErrorMessage(error);

    toastStore.addError({
      message,
      detail,
      duration: 5000,
    });
  };

  const isValidRoute = computed(() => {
    // A valid route needs at least 2 points
    if (routeCoordinates.value.length < 2) {
      return false;
    }

    // We need to have a path from graphhopper to save the route
    return routeFeature.value !== null;
  });

  const updatePointStyle = (
    feature: Feature,
    state: 'default' | 'hover' | 'highlighted',
  ): void => {
    const pointIndex = feature.get('pointIndex') as number | undefined;
    if (pointIndex !== undefined) {
      const pointText = `${pointIndex + 1}`;
      feature.setStyle(getRoutePointStyle(pointText, state));
    }
  };

  const movePoint = (index: number, newOlCoordinate: Coordinate): void => {
    if (index < 0 || index >= routeCoordinates.value.length) {
      return;
    }

    routeCoordinates.value[index] = olToNamedLatLonCoordinate(newOlCoordinate);
    const feature = pointFeatures.value.at(index);
    if (!feature) {
      return;
    }
    feature.setGeometry(new Point(newOlCoordinate));
    pointFeatures.value.forEach((feature, idx) => {
      const pointText = `${idx + 1}`;
      feature.setStyle(getRoutePointStyle(pointText));
    });
  };

  const deletePoint = (
    index: number,
    editingVectorSource: VectorSource,
  ): void => {
    if (index < 0 || index >= routeCoordinates.value.length) {
      return;
    }

    routeCoordinates.value.splice(index, 1);
    const featureToDelete = pointFeatures.value.splice(index, 1)[0];
    if (isFeature(featureToDelete)) {
      editingVectorSource.removeFeature(featureToDelete);
    }

    pointFeatures.value.forEach((feature, idx) => {
      const pointText = `${idx + 1}`;
      feature.set('pointIndex', idx);
      feature.setStyle(getRoutePointStyle(pointText));
    });
  };

  const updateRouteCoordinates = (coordinates: NamedLatLonCoordinate[]): void => {
    routeCoordinates.value = [...coordinates];
  };

  const updatePointFeatures = (editingVectorSource: VectorSource): void => {
    // Remove existing point features from source
    pointFeatures.value.forEach((feature) => {
      if (isFeature(feature)) {
        editingVectorSource.removeFeature(feature);
      }
    });

    pointFeatures.value = [];
    hoveredPointFeature.value = null;

    routeCoordinates.value.forEach((geoCoordinate, index) => {
      const olCoordinate = namedLatLonCoordinateToOlCoordinate(geoCoordinate);
      const pointFeature = new Feature({
        geometry: new Point(olCoordinate),
      });

      const pointText = `${index + 1}`;
      pointFeature.set('routePoint', true);
      pointFeature.set('pointIndex', index);
      pointFeature.setStyle(getRoutePointStyle(pointText));
      pointFeatures.value.push(pointFeature);
      editingVectorSource.addFeature(pointFeature);
    });
  };

  const getRouteOrThrow = async (coordinates: NamedLatLonCoordinate[]): Promise<GetRoute200Response> => {
    if (coordinates.length < 2) {
      throw new RouteError('At least two points are required to get a route.', RouteErrorType.NotEnoughPoints);
    }

    try {
      return await api.unwrap(
        api.geo.getRoute({
          getRouteRequestDTO: {
            points: coordinates.map(coordinate => ({
              latitude: coordinate.latitude,
              longitude: coordinate.longitude,
            })),
          },
        }, {
          'axios-retry': {
            retries: 0,
          },
        }),
      );
    }
    catch (error) {
      throw createRouteErrorFromApiResponse(error);
    }
  };

  const decodePolyline = (polylineString: string): Feature<Geometry> | null => {
    const polylineFormat = new Polyline({
      factor: 1e5,
    });

    const coordinates = polylineFormat.readGeometry(polylineString, {
      dataProjection: NoraSupportedCoordinateFormats.WGS84,
      featureProjection: NoraSupportedCoordinateFormats.WEB_MERCATOR,
    });

    return new Feature({
      geometry: coordinates,
    });
  };

  const deleteRouteFeature = (editingVectorSource: VectorSource): void => {
    if (isFeature(routeFeature.value)) {
      editingVectorSource.removeFeature(routeFeature.value);
      routeFeature.value = null;
    }
  };

  const addPoint = (namedLatLonCoordinate: NamedLatLonCoordinate): void => {
    routeCoordinates.value.push(namedLatLonCoordinate);
    if (viewMode.value === RouteViewMode.Address) {
      viewMode.value = RouteViewMode.PointList;
    }
  };

  const updateRouteFeature = (
    routeGeometry: Feature<Geometry>,
    editingVectorSource: VectorSource,
    getDefaultProperties: () => Partial<GeoJsonStylePropertiesDTO>,
  ): void => {
    if (isFeature(routeFeature.value)) {
      editingVectorSource.removeFeature(routeFeature.value);
    }

    routeFeature.value = routeGeometry;
    routeFeature.value.set('coordinates', toRaw(routeCoordinates.value));

    if (isNonEmptyString(featureId.value)) {
      routeFeature.value.set('id', toRaw(featureId.value));
    }

    const styleProperties = getDefaultProperties();
    routeFeature.value.setProperties({
      ...styleProperties,
    });

    routeFeature.value.setStyle(getFeatureStyle);
    if (isFeature(routeFeature.value)) {
      editingVectorSource.addFeature(toRaw(routeFeature.value));
    }
  };

  const loadFeature = (
    feature: Feature<Geometry>,
    editingVectorSource: VectorSource,
    getDefaultProperties: () => {
      [x: string]: unknown;
    },
  ): boolean => {
    const routePoints = feature.get('coordinates') as NamedLatLonCoordinate[] | undefined;
    if (!Array.isArray(routePoints) || routePoints.length < 2) {
      return false;
    }

    featureId.value = feature.get('id') as string | undefined;
    resetRoute();
    routeCoordinates.value = [...routePoints];
    updatePointFeatures(editingVectorSource);

    routeFeature.value = feature.clone();
    routeFeature.value.setProperties({
      ...getDefaultProperties(),
      route: true,
      coordinates: toRaw(routeCoordinates.value),
    });
    routeFeature.value.setStyle(getFeatureStyle);

    if (!isFeature(routeFeature.value)) {
      return false;
    }

    editingVectorSource.addFeature(toRaw(routeFeature.value));
    return true;
  };

  const updateRoute = async (
    editingVectorSource: VectorSource,
    getDefaultProperties: () => Partial<GeoJsonStylePropertiesDTO>,
  ): Promise<void> => {
    try {
      clearErrors();

      // If we don't have at least 2 points, we can't make a route
      if (routeCoordinates.value.length < 2) {
        deleteRouteFeature(editingVectorSource);
        return;
      }

      const routeData = await getRouteOrThrow(routeCoordinates.value);
      const firstPath = routeData.data?.paths[0]?.points;
      if (!isNonEmptyString(firstPath)) {
        throw new RouteError('Invalid route data received', RouteErrorType.RuntimeError);
      }

      const routeGeometry = decodePolyline(firstPath);
      if (!routeGeometry) {
        throw new RouteError('Failed to decode route geometry', RouteErrorType.RuntimeError);
      }

      updateRouteFeature(
        routeGeometry,
        editingVectorSource,
        getDefaultProperties,
      );

      // Clear error state on successful route update
      clearErrors();
    }
    catch (error) {
      if (error instanceof RouteError) {
        currentError.value = error;
        showRouteErrorToast(error);
      }
      else {
        // Fallback for non-RouteError exceptions
        toastStore.addError({
          message: t('drawTools.route.errors.unknown'),
          detail: t('drawTools.route.errors.unknownDetail'),
          duration: 5000,
        });
      }

      deleteRouteFeature(editingVectorSource);
    }
  };

  return {
    addPoint,
    clearErrors,
    currentError,
    decodePolyline,
    deletePoint,
    deleteRouteFeature,
    errorMessage,
    featureId,
    getRouteOrThrow,
    hoveredPointFeature,
    isValidRoute,
    loadFeature,
    movePoint,
    namedLatLonCoordinateToOlCoordinate,
    olToNamedLatLonCoordinate,
    pointFeatures,
    reset,
    resetRoute,
    routeCoordinates,
    routeFeature,
    showRouteErrorToast,
    updatePointFeatures,
    updatePointStyle,
    updateRoute,
    updateRouteCoordinates,
    updateRouteFeature,
    viewMode,
  };
}

export function useRouteToolStore(storeId?: string | null) {
  if (!isNonEmptyString(storeId)) {
    return defineStore(`routeToolStore`, routeToolStore)();
  }

  return defineStore(`routeToolStore-${storeId}`, routeToolStore)();
}

export default useRouteToolStore;
